<template>
  <CommonPages id="detail" class="detail" headerTheme="dark" :titlePrefix="formattedCarName"
    :metaDescription="description">
    <div class="detail__container">
      <!-- 加载状态 - 临时隐藏 -->
      <!-- <div v-if="loading" class="detail__loading">
        <div class="detail__loading-text">Loading car information...</div>
      </div> -->

      <!-- 错误状态 -->
      <div v-if="error" class="detail__error">
        <div class="detail__error-message">{{ errorMessage }}</div>
        <button class="detail__error-retry" @click="fetchCarInfo">Retry</button>
      </div>

      <!-- 正常内容显示 -->
      <template v-else>
        <!-- 顶部车辆信息 -->
        <div class="detail__header">
          <div class="detail__header-content">
            <h1 class="detail__title">{{ carData?.model || 'Loading...' }}</h1>
            <div class="detail__brand">
              <img :src="carData?.logo || ''" class="detail__brand-logo" alt="">
              <span class="detail__brand-name">{{ carData?.brand || '' }}</span>
            </div>
          </div>
        </div>

        <!-- 主要内容区域 -->
        <div class="detail__content">
          <!-- 左侧主要内容 -->
          <div class="detail__main">
            <!-- 图库区域 -->
            <div class="detail__gallery">
              <!-- 主图显示 -->
              <div class="detail__gallery-main" v-if="carData?.gallery?.length > 0"
                @click="scrollToSimilarCars">
                <img :src="currentImage" alt="" class="detail__gallery-img">
              </div>

              <!-- 缩略图导航 -->
              <div class="detail__thumbnails" v-if="carData?.gallery?.length > 0">
                <div v-for="(image, index) in carData?.gallery.slice(0, 4)" :key="`thumb-${index}`"
                  class="detail__thumbnail-item"
                  :class="{'detail__thumbnail-item--active': currentImageIndex === index}"
                  @click="setCurrentImage(index)">
                  <img :src="image" alt="" class="detail__thumbnail-img">
                </div>
              </div>
            </div>

            <AdComponent v-show="$global.isObjectNotEmpty(adsensConfig) && adsensConfig?.detail_1"
              ref="ads-detail-1" class="ad-width-100 pc_padding m-t-40 m-b-40"
              :ads="adsensConfig?.detail_1" :showDebug="showDebug" />

            <!-- 移动端视图下的规格信息区域 -->
            <div class="detail__mobile-sidebar" v-if="deviceType === 'mobile'">
              <div class="detail__sidebar-card">
                <div class="detail__spec-item">
                  <div class="detail__spec-icon-wrapper">
                    <img src="@/assets/detail-icons/traffic_jam.svg" class="detail__spec-icon"
                      alt="">
                  </div>
                  <div class="detail__spec-content">
                    <div class="detail__spec-label">Type</div>
                    <div class="detail__spec-value">{{ carData?.category || '-' }}</div>
                  </div>
                </div>

                <div class="detail__spec-item">
                  <div class="detail__spec-icon-wrapper">
                    <img src="@/assets/detail-icons/calendar_month.svg" class="detail__spec-icon"
                      alt="">
                  </div>
                  <div class="detail__spec-content">
                    <div class="detail__spec-label">Release Date</div>
                    <div class="detail__spec-value">{{ carData?.year || '-' }}</div>
                  </div>
                </div>

                <div class="detail__spec-item">
                  <div class="detail__spec-icon-wrapper">
                    <img src="@/assets/detail-icons/paid.svg" class="detail__spec-icon" alt="">
                  </div>
                  <div class="detail__spec-content">
                    <div class="detail__spec-label">Price Range</div>
                    <div class="detail__spec-value">{{ carData?.price || '-' }}</div>
                  </div>
                </div>

                <div class="detail__spec-item">
                  <div class="detail__spec-icon-wrapper">
                    <img src="@/assets/detail-icons/local_gas_station.svg" class="detail__spec-icon"
                      alt="">
                  </div>
                  <div class="detail__spec-content">
                    <div class="detail__spec-label">Fuel</div>
                    <div class="detail__spec-value">{{ carData?.fuel || '-' }}</div>
                  </div>
                </div>

                <div class="detail__spec-item">
                  <div class="detail__spec-icon-wrapper">
                    <img src="@/assets/detail-icons/crossword.svg" class="detail__spec-icon" alt="">
                  </div>
                  <div class="detail__spec-content">
                    <div class="detail__spec-label">Body Type</div>
                    <div class="detail__spec-value">{{ carData?.bodyType || '-' }}</div>
                  </div>
                </div>

                <div class="detail__spec-item">
                  <div class="detail__spec-icon-wrapper">
                    <img src="@/assets/detail-icons/award_star.svg" class="detail__spec-icon"
                      alt="">
                  </div>
                  <div class="detail__spec-content">
                    <div class="detail__spec-label">Rating</div>
                    <div class="detail__spec-value">{{ carData?.rate || '-' }}</div>
                  </div>
                </div>

                <div class="detail__spec-item">
                  <div class="detail__spec-icon-wrapper">
                    <img src="@/assets/detail-icons/readiness_score.svg" class="detail__spec-icon"
                      alt="">
                  </div>
                  <div class="detail__spec-content">
                    <div class="detail__spec-label">0-60 MPH</div>
                    <div class="detail__spec-value">{{ carData?.speedValue || '-' }}</div>
                  </div>
                </div>
              </div>
            </div>

            <div class="detail__mobile-similar-pointer"></div>
            <!-- 移动端第一部分相似价格车辆 - 横向滚动样式 -->
            <div class="detail__mobile-similar-horizontal" v-if="deviceType === 'mobile'">
              <div class="detail__similar-horizontal-title">
                <h2>Similar Priced Cars</h2>
                <div class="detail__view-all">
                  View All
                  <img src="@/assets/view-all.svg" alt="view all" @click="gotoAll()">
                </div>
              </div>

              <!-- 水平滚动列表 -->
              <div class="detail__similar-horizontal-list">
                <a v-for="(car, index) in similarCars.slice(0, 4)"
                  :key="`similar-horizontal-${index}`" class="detail__similar-horizontal-item"
                  :href="getCarDetailUrl(car)">
                  <div class="detail__similar-horizontal-image">
                    <img :src="car.image" alt="">
                    <div class="detail__similar-horizontal-speed">
                      {{ car.speedValue }}
                      <span>0-60-MPH Times</span>
                    </div>
                  </div>
                  <div class="detail__similar-horizontal-info">
                    <div class="detail__similar-horizontal-header">
                      <div>
                        <div class="detail__similar-horizontal-model">{{ car.model }}</div>
                        <div class="detail__similar-horizontal-brand">{{ car.brand }}</div>
                      </div>
                      <img :src="car.logo" class="detail__similar-horizontal-logo" alt="">
                    </div>
                    <div class="detail__similar-horizontal-rating">
                      <img src="@/assets/star.svg" alt="">
                      <span>Rating: <span
                          class="detail__similar-horizontal-rate">{{ car.rate || '4.8' }}</span></span>
                    </div>
                    <div class="detail__similar-horizontal-price">
                      <div class="detail__similar-horizontal-price-text">{{ car.price }}</div>
                      <div class="detail__similar-horizontal-price-link"
                        @click.stop="gotoDetail(car)">
                        <img src="@/assets/arrow_outward.svg" alt="">
                      </div>
                    </div>
                  </div>
                </a>
              </div>
            </div>

            <AdComponent v-show="$global.isObjectNotEmpty(adsensConfig) && adsensConfig?.detail_2"
              ref="ads-detail-2" class="ad-width-100 pc_padding m-t-40 m-b-40"
              :ads="adsensConfig?.detail_2" :showDebug="showDebug" />

            <!-- 详情内容区域 -->
            <div class="detail__sections">
              <div class="detail__timeline">
                <div class="detail__timeline-line"></div>
              </div>

              <div class="detail__sections-content">
                <div v-for="(section, index) in sections" :key="`section-content-${index}`"
                  class="detail__section">
                  <div class="detail__section-header">
                    <div class="detail__timeline-dot"
                      :class="{'detail__timeline-dot--active': index === activeSection}"></div>
                    <h2 class="detail__section-title">{{ section.title }}</h2>
                  </div>
                  <div class="detail__section-content" v-html="section.content"></div>
                </div>
              </div>
            </div>

            <!-- 移动端第二部分相似价格车辆 - 垂直列表样式 -->
            <div class="detail__mobile-similar-vertical" v-if="deviceType === 'mobile'">
              <div class="detail__similar-vertical-title">
                <h2>Similar Priced Cars</h2>
                <div class="detail__view-all">
                  View All
                  <img src="@/assets/view-all.svg" alt="view all" @click="gotoAll()">
                </div>
              </div>

              <!-- 将 SimilarPricedCars 组件替换回原来的垂直列表结构 -->
              <div class="detail__similar-vertical-list">
                <a v-for="(car, index) in similarCars.slice(4, 8)"
                  :key="`similar-vertical-${index}`" class="detail__similar-vertical-item"
                  :href="getCarDetailUrl(car)">
                  <div class="detail__similar-vertical-image">
                    <img :src="car.image" alt="" class="detail__similar-vertical-pic">
                    <div class="detail__similar-vertical-speed">
                      <div class="detail__similar-vertical-speed-value">
                        <div class="detail__similar-vertical-speed-number">{{ car.speedValue }}
                        </div>
                      </div>
                      <div class="detail__similar-vertical-speed-label">
                        <div class="detail__similar-vertical-speed-text">0-60-MPH Times</div>
                      </div>
                    </div>
                  </div>
                  <div class="detail__similar-vertical-baseinfo">
                    <div class="detail__similar-vertical-info">
                      <div class="detail__similar-vertical-card-title">
                        <div class="detail__similar-vertical-name">
                          <div class="detail__similar-vertical-model">{{ car.model }}</div>
                          <div class="detail__similar-vertical-brand">{{ car.brand }}</div>
                        </div>
                        <img :src="car.logo" class="detail__similar-vertical-logo" alt="">
                      </div>

                      <div class="detail__similar-vertical-divider"></div>

                      <div class="detail__similar-vertical-type">
                        <div class="detail__similar-vertical-type-item">
                          <img src="@/assets/calendar.svg"
                            class="detail__similar-vertical-type-icon" alt="">
                          <div class="detail__similar-vertical-type-text">{{ car.year || '2025' }}
                          </div>
                        </div>
                        <div class="detail__similar-vertical-type-item">
                          <img src="@/assets/car-type.svg"
                            class="detail__similar-vertical-type-icon" alt="">
                          <div class="detail__similar-vertical-type-text">
                            {{ car.category || 'Luxury vehicle' }}</div>
                        </div>
                        <div class="detail__similar-vertical-type-item">
                          <img src="@/assets/body-type.svg"
                            class="detail__similar-vertical-type-icon" alt="">
                          <div class="detail__similar-vertical-type-text">
                            {{ car.bodyType || 'Sedan' }}</div>
                        </div>
                      </div>

                      <div class="detail__similar-vertical-divider"></div>

                      <div class="detail__similar-vertical-price">
                        <div class="detail__similar-vertical-price-text">{{ car.price }}</div>
                        <div class="detail__similar-vertical-price-link">
                          <img src="@/assets/arrow_outward.svg"
                            class="detail__similar-vertical-arrow" alt="">
                        </div>
                      </div>
                    </div>
                  </div>
                </a>
              </div>
            </div>
          </div>

          <!-- 右侧信息栏 (仅在PC端显示) -->
          <div class="detail__sidebar" v-if="deviceType !== 'mobile'">
            <div class="detail__sidebar-card">
              <div class="detail__spec-item">
                <div class="detail__spec-icon-wrapper">
                  <img src="@/assets/detail-icons/traffic_jam.svg" class="detail__spec-icon" alt="">
                </div>
                <div class="detail__spec-content">
                  <div class="detail__spec-label">Type</div>
                  <div class="detail__spec-value">{{ carData?.category || '-' }}</div>
                </div>
              </div>

              <div class="detail__spec-item">
                <div class="detail__spec-icon-wrapper">
                  <img src="@/assets/detail-icons/calendar_month.svg" class="detail__spec-icon"
                    alt="">
                </div>
                <div class="detail__spec-content">
                  <div class="detail__spec-label">Release Date</div>
                  <div class="detail__spec-value">{{ carData?.year || '-' }}</div>
                </div>
              </div>

              <div class="detail__spec-item">
                <div class="detail__spec-icon-wrapper">
                  <img src="@/assets/detail-icons/paid.svg" class="detail__spec-icon" alt="">
                </div>
                <div class="detail__spec-content">
                  <div class="detail__spec-label">Price Range</div>
                  <div class="detail__spec-value">{{ carData?.price || '-' }}</div>
                </div>
              </div>

              <div class="detail__spec-item">
                <div class="detail__spec-icon-wrapper">
                  <img src="@/assets/detail-icons/local_gas_station.svg" class="detail__spec-icon"
                    alt="">
                </div>
                <div class="detail__spec-content">
                  <div class="detail__spec-label">Fuel</div>
                  <div class="detail__spec-value">{{ carData?.fuel || '-' }}</div>
                </div>
              </div>

              <div class="detail__spec-item">
                <div class="detail__spec-icon-wrapper">
                  <img src="@/assets/detail-icons/crossword.svg" class="detail__spec-icon" alt="">
                </div>
                <div class="detail__spec-content">
                  <div class="detail__spec-label">Body Type</div>
                  <div class="detail__spec-value">{{ carData?.bodyType || '-' }}</div>
                </div>
              </div>

              <div class="detail__spec-item">
                <div class="detail__spec-icon-wrapper">
                  <img src="@/assets/detail-icons/award_star.svg" class="detail__spec-icon" alt="">
                </div>
                <div class="detail__spec-content">
                  <div class="detail__spec-label">Rating</div>
                  <div class="detail__spec-value">{{ carData?.rate || '-' }}</div>
                </div>
              </div>

              <div class="detail__spec-item">
                <div class="detail__spec-icon-wrapper">
                  <img src="@/assets/detail-icons/readiness_score.svg" class="detail__spec-icon"
                    alt="">
                </div>
                <div class="detail__spec-content">
                  <div class="detail__spec-label">0-60 MPH</div>
                  <div class="detail__spec-value">{{ carData?.speedValue || '-' }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- 相似价格车辆推荐模块 (仅在PC端显示) -->
    <SimilarPricedCars v-if="deviceType !== 'mobile' && carData && carData.brand"
      :carList="similarCars" :getCarDetailUrl="getCarDetailUrl" />

  </CommonPages>
</template>

<script>
import common from '@/mixin/common.js'
import adMixin from '@/mixin/adMixin.js'
import adTrackingMixin from '@/mixin/adTrackingMixin.js'
import reportMixin from '@/mixin/reportMixin.js'
import { mapGetters } from 'vuex';
import CommonPages from '@/pages/components/page_common.vue'
import SimilarPricedCars from '@/components/SimilarPricedCars.vue'
import AdComponent from '@/pages/components/AdComponent.vue'

export default {
  name: 'Detail',
  components: {
    CommonPages,
    AdComponent,
    SimilarPricedCars
  },
  mixins: [common, adMixin, adTrackingMixin, reportMixin],
  props: {
    carName: {
      type: String,
      default: ''
    },
    id: {
      type: String,
      default: ''
    }
  },
  computed: {
    ...mapGetters({
      deviceType: 'deviceType',
      adsensConfig: 'adsensConfig',
    }),
    formattedCarName () {
      // 将URL中的连字符替换回空格并进行格式化
      return this.carName ? decodeURIComponent(this.carName).replace(/-/g, ' ') : '';
    },
    description () {
      return `${this.domainConfig?.['meta']?.des || 'Default des'}`
    },
    currentImage () {
      if (!this.carData || !this.carData.gallery || !Array.isArray(this.carData.gallery) || this.carData.gallery.length === 0) {
        return '';
      }
      const index = Math.min(this.currentImageIndex, this.carData.gallery.length - 1);
      return this.carData.gallery[index] || '';
    },
    galleryLength () {
      return this.carData && this.carData.gallery ? this.carData.gallery.length : 0;
    }
  },
  data () {
    return {
      currentImageIndex: 0,
      activeSection: 0,
      loading: false,
      error: null,
      carData: {
        gallery: [], // 初始化空数组，避免在数据加载前引用报错
        model: '',
        brand: '',
        logo: '',
        year: '',
        category: '',
        bodyType: '',
        fuel: '',
        price: '',
        rate: '',
        speedValue: ''
      },
      similarCars: [],
      sections: [],
      isLoading: true,
      scrollTop: 0
    }
  },
  beforeMount () {
    // 保存上一个页面的路径和参数到Vuex
    this.saveNavigationState();
  },
  async mounted () {
    // 获取车辆详情数据
    this.fetchCarInfo();

    await this.initAdSystem();
    // 确保在广告系统初始化后再初始化跟踪
    this.$nextTick(() => {
      this.initAdTracking();
      setTimeout(() => {
        this.showPos();
      }, 1000); // 延迟1秒执行，确保页面元素已完全加载
    });
    // 使用防抖包装滚动事件
    this.onScroll = this.debounce(this.onScroll.bind(this), 300);
    // 监听滚动事件
    window.addEventListener('scroll', this.onScroll);
    // 监听滚动事件以更新活动章节
    window.addEventListener('scroll', this.handleScroll);

  },
  beforeDestroy () {
    // 组件销毁时移除滚动事件监听
    window.removeEventListener('scroll', this.onScroll);
  },
  methods: {
    // 优化后的滚动方法
    onScroll () {
      // 在滚动时执行 showPos 方法，获取当前可视范围内的广告坐标
      this.showPos();
    },
    // 防抖函数
    debounce (func, delay) {
      let timeout;
      return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          func.apply(this, args);
        }, delay);
      };
    },
    // 显示当前屏幕可视范围内的广告坐标
    showPos () {
      console.log('执行showPos方法，显示当前可视范围内的广告坐标');

      // 获取所有广告元素
      const adElements = document.querySelectorAll('ins.adsbygoogle');
      const visibleAds = [];

      // 当前屏幕可视区域信息
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      // 遍历所有广告元素，找出在可视范围内的
      adElements.forEach((adElement) => {
        // 使用 getBoundingClientRect 获取元素相对于视口的位置
        const rect = adElement.getBoundingClientRect();

        // 检查元素是否在视口中可见（至少部分可见）
        // rect.top 和 rect.left 是相对于视口左上角的坐标
        const isVisible = !(
          rect.bottom < 0 ||   // 元素底部在视口顶部以上
          rect.top > windowHeight ||  // 元素顶部在视口底部以下
          rect.right < 0 ||  // 元素右侧在视口左侧以外
          rect.left > windowWidth  // 元素左侧在视口右侧以外
        );

        if (isVisible) {
          // 保持与 setupIframeTracking 方法中相同的数据格式
          const transformData = {
            insX: Math.round(rect.left),
            insY: Math.round(rect.top),
            insHeight: Math.round(rect.height),
            windowWidth: windowWidth,
            windowHeight: windowHeight
          };

          visibleAds.push(transformData);
        }
      });

      console.log('可视范围内的广告数量:', visibleAds.length);
      console.log('可视范围内的广告坐标:', visibleAds);

      // 即使没有可见广告，也会发送空数组
      this.$adPosition(JSON.stringify(visibleAds))
      return visibleAds;
    },
    // 保存导航状态到vuex
    saveNavigationState () {
      // 获取前一个页面路径
      const previousPage = document.referrer;

      // 检查是否从all.vue页面来的
      const isFromAll = previousPage &&
        (previousPage.includes('/car-list') || previousPage.includes('/all'));

      if (isFromAll) {
        // 如果是从列表页来的，记录状态
        this.$store.commit('SET_FROM_DETAIL', true);
        this.$store.commit('SET_LAST_VISITED_PAGE', previousPage);

        // 保存查询参数
        const queryParams = { ...this.$route.query };
        this.$store.commit('SET_QUERY_PARAMS', queryParams);
      }
    },

    // 获取车辆详情
    async fetchCarInfo () {
      if (!this.id) {
        console.error('[Detail] Missing car ID');
        return;
      }

      try {
        this.loading = true;
        this.error = null;
        const themeFilters = this.$store.state.domainConfig?.themeFilters || {};

        console.log(themeFilters);
        const res = await this.$global.server.getCarInfo({ id: this.id, ...themeFilters });

        if (res.code === 200 && res.data) {
          console.log('[Detail] Car detail data fetched successfully:', res.data);

          // 从接口返回的carInfo获取车辆详情
          const carInfo = res.data.carInfo || res.data;

          // Extract gallery images
          const photos = carInfo.photos ? carInfo.photos.split(',') : [];
          const gallery = photos.length > 0 ?
            photos : [require('@/assets/car-recommend-img.png')];

          // 创建新的carData对象，保留默认值结构
          this.carData = {
            gallery: gallery, // 确保gallery总是一个数组
            id: carInfo.id || '',
            model: carInfo.name || 'Unknown Model',
            brand: carInfo.brand || 'Unknown Brand',
            logo: carInfo.logoUrl || require('@/assets/car-logo.svg'), // 优先使用接口返回的logo，没有则使用默认logo
            year: carInfo.marketTime || carInfo.year || '-',
            category: carInfo.purpose || '-',
            bodyType: carInfo.bodyType || carInfo.type || '-',
            purpose: carInfo.purpose || '-',
            fuel: carInfo.fuel || '-',
            price: this.formatPrice(carInfo.leastPrice, carInfo.mostPrice) || '-',
            rate: carInfo.rate || '0',
            speedValue: carInfo.accelerationTime ? `${carInfo.accelerationTime}s` : '-'
          };

          // 初始化sections为空数组
          this.sections = [];

          // 将API返回的各字段转换为sections数组
          const sectionFields = [
            { field: 'overall', title: 'Overview' },
            { field: 'endurance', title: 'Endurance' },
            { field: 'safety', title: 'Safety' },
            { field: 'fuelEconomy', title: 'Fuel Economy' },
            { field: 'interiorAndComfort', title: 'Interior and Comfort' },
            { field: 'infotainmentAndConnectivity', title: 'Infotainment and Connectivity' },
            { field: 'warrantyAndMaintenanceCoverage', title: 'Warranty and Maintenance Coverage' }
          ];

          // 将每个字段转换为section对象
          sectionFields.forEach(section => {
            if (carInfo[section.field] && carInfo[section.field].trim()) {
              this.sections.push({
                title: section.title,
                content: carInfo[section.field]
              });
            }
          });

          // 重置图片显示索引
          this.currentImageIndex = 0;

          // 从接口返回的recommendList获取相似车辆
          if (res.data.recommendList && Array.isArray(res.data.recommendList)) {
            // 处理API返回的数据
            this.similarCars = res.data.recommendList.map(item => this.formatCarData(item));
            console.log(`[Detail] 获取到相似车辆: ${this.similarCars.length}`);
          } else {
            const type = carInfo.type || '';
            const purpose = carInfo.purpose || '';
            const fuel = carInfo.fuel || '';

            console.log(`[Detail] 提取关键字段重新请求推荐: type=${type}, purpose=${purpose}, fuel=${fuel}`);

            // 使用提取的字段再次请求获取推荐列表
            await this.fetchRecommendList(type, purpose, fuel);
          }

        } else {
          throw new Error(res.message || 'Failed to fetch car details');
        }
      } catch (error) {
        console.error('[Detail] Error fetching car details:', error);
        this.error = error.message || 'Failed to fetch car details';

        // 确保carData保持一个有效的结构，即使请求失败
        this.carData = {
          gallery: [require('@/assets/car-recommend-img.png')],
          model: 'Unknown Model',
          brand: 'Unknown Brand',
          logo: require('@/assets/car-logo.svg'),
          year: '-',
          category: '-',
          bodyType: '-',
          purpose: '-',
          fuel: '-',
          price: '-',
          rate: '0',
          speedValue: '-'
        };
      } finally {
        this.loading = false;
      }
    },

    // 使用关键字段请求推荐列表
    async fetchRecommendList (type, purpose, fuel) {
      try {
        console.log('[Detail] 请求相关推荐列表');

        // 构建请求参数
        const params = {
          id: this.id,
          type: type,
          purpose: purpose,
          fuel: fuel
        };

        // 使用修改后的参数再次请求getCarInfo接口
        const response = await this.$global.server.getCarInfo(params);

        if (response && response.code === 200 && response.data) {
          // 检查返回的数据中是否包含recommendList
          if (response.data.recommendList && Array.isArray(response.data.recommendList)) {
            this.similarCars = response.data.recommendList.map(item => this.formatCarData(item));
            console.log(`[Detail] 根据关键字段获取到相似车辆: ${this.similarCars.length}`);
          } else {
            console.log('[Detail] 第二次请求未返回推荐列表，尝试获取相似车辆');
            this.fetchSimilarCars();
          }
        } else {
          console.warn('[Detail] 第二次请求获取推荐失败，尝试获取相似车辆');
          this.fetchSimilarCars();
        }
      } catch (error) {
        console.error('[Detail] 获取推荐列表出错:', error);
        // 如果获取推荐列表失败，尝试获取相似车辆
        this.fetchSimilarCars();
      }
    },

    // 格式化价格显示
    formatPrice (leastPrice, mostPrice) {
      if (!leastPrice && !mostPrice) return null;

      const formatNumber = (num) => {
        if (!num) return null;
        // 将价格格式化为$xx,xxx格式
        return '$' + num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      };

      if (leastPrice && mostPrice) {
        // 有最低价和最高价，显示价格区间
        return `${formatNumber(leastPrice)}-${formatNumber(mostPrice)}`;
      } else if (leastPrice) {
        // 只有最低价
        return `>${formatNumber(leastPrice)}`;
      } else if (mostPrice) {
        // 只有最高价
        return `<${formatNumber(mostPrice)}`;
      }

      return null;
    },

    setCurrentImage (index) {
      this.currentImageIndex = index;
    },
    handleScroll () {
      // 基于滚动位置更新活动章节的逻辑
      const sections = document.querySelectorAll('.detail__section');
      const scrollPosition = window.scrollY + window.innerHeight / 3;

      sections.forEach((section, index) => {
        const top = section.offsetTop;
        const bottom = top + section.offsetHeight;

        if (scrollPosition >= top && scrollPosition < bottom) {
          this.activeSection = index;
        }
      });
    },

    // 跳转到全部车辆页面
    gotoAll () {
      // 获取当前路由的channel参数
      const currentChannel = this.$router.currentRoute.params.channel;

      // 获取当前URL的查询参数
      const currentQuery = { ...this.$route.query };
      const queryString = Object.keys(currentQuery).length > 0
        ? `?${new URLSearchParams(currentQuery).toString()}`
        : '';

      // 如果存在channel参数，则构建带channel的路径
      if (currentChannel) {
        // 构建完整URL
        const baseUrl = window.location.origin;
        const fullPath = `${baseUrl}/${currentChannel}/car-list${queryString}`;

        // 使用location跳转
        window.location.href = fullPath;
      } else {
        // 没有channel参数，使用常规router跳转
        this.$router.push({
          path: '/car-list',
          query: currentQuery
        });
      }
    },

    // 生成车辆详情页的URL
    getCarDetailUrl (car) {
      try {
        // 检查是否有必要的参数
        if (!car || !car.id) {
          console.warn('[Detail] Missing car data for detail page link');
          return '#';
        }

        // 获取当前路由的channel参数
        const currentChannel = this.$router.currentRoute.params.channel;

        // 格式化车辆名称，去除空格，用连字符替代
        const formattedName = encodeURIComponent((car.model || car.name || '').replace(/\s+/g, '-'));

        // 获取当前URL的查询参数
        const currentQuery = { ...this.$route.query };
        const queryString = Object.keys(currentQuery).length > 0
          ? `?${new URLSearchParams(currentQuery).toString()}`
          : '';

        // 如果存在channel参数，则构建带channel的路径
        if (currentChannel) {
          // 构建完整URL
          const baseUrl = window.location.origin;
          const fullPath = `${baseUrl}/${currentChannel}/cars/${formattedName}/${car.id}${queryString}`;
          return fullPath;
        } else {
          // 没有channel参数，使用常规router跳转
          const path = `/cars/${formattedName}/${car.id}`;
          return `${path}${queryString}`;
        }
      } catch (error) {
        console.error('[Detail] Detail navigation URL generation error:', error);
        return '#';
      }
    },

    // 跳转到车辆详情页面
    gotoDetail (car) {
      try {
        // 检查是否有必要的参数
        if (!car || !car.id) {
          console.warn('[Detail] Missing car data for detail page');
          return;
        }

        // 获取当前路由的channel参数
        const currentChannel = this.$router.currentRoute.params.channel;

        // 格式化车辆名称，去除空格，用连字符替代
        const formattedName = encodeURIComponent((car.model || car.name || '').replace(/\s+/g, '-'));

        // 获取当前URL的查询参数
        const currentQuery = { ...this.$route.query };
        const queryString = Object.keys(currentQuery).length > 0
          ? `?${new URLSearchParams(currentQuery).toString()}`
          : '';

        // 如果存在channel参数，则构建带channel的路径
        if (currentChannel) {
          // 构建完整URL
          const baseUrl = window.location.origin;
          const fullPath = `${baseUrl}/${currentChannel}/cars/${formattedName}/${car.id}${queryString}`;

          // 使用location跳转，保留所有参数
          window.location.href = fullPath;
        } else {
          // 没有channel参数，使用常规router跳转
          this.$router.push({
            path: `/cars/${formattedName}/${car.id}`,
            query: currentQuery
          });
        }
      } catch (error) {
        console.error('[Detail] Detail navigation error:', error);
      }
    },

    // 格式化车辆数据
    formatCarData (item) {
      // 格式化车辆数据以便在页面上显示
      const formattedItem = { ...item };
      formattedItem.logo = item.logo || require('@/assets/car-logo.svg');

      // // 处理图片
      // if (item.photos && item.photos.length > 0) {
      //   // 如果photos是字符串（逗号分隔的URL列表），则转换为数组
      //   if (typeof item.photos === 'string') {
      //     const photoArray = item.photos.split(',');
      //     formattedItem.image = photoArray[0]; // 使用第一张图片作为主图
      //   } else {
      //     formattedItem.image = item.photos[0];
      //   }
      // } else {
      //   // 使用默认图片
      //   formattedItem.image = require('@/assets/car-recommend-img.png');
      // }

      formattedItem.image = item.cover;

      // 处理模型名称
      if (!formattedItem.model && item.name) {
        formattedItem.model = item.name;
      }

      // 确保category和bodyType有值
      formattedItem.category = item.purpose || item.type || 'N/A';
      formattedItem.bodyType = item.type || formattedItem.category || 'N/A';

      // 处理年份
      formattedItem.year = item.year || item.marketTime?.substring(0, 4) || '-';

      // 处理价格
      formattedItem.price = this.formatPrice(item.leastPrice, item.mostPrice) || item.price || '-';

      // 处理加速时间
      formattedItem.speedValue = item.accelerationTime ? `${item.accelerationTime}s` : '-';

      // 处理评分
      formattedItem.rate = item.rate || '4.5';

      return formattedItem;
    },

    // 获取相似车辆
    async fetchSimilarCars () {
      try {
        // 构建API参数
        const params = {
          pageSize: 8,
          pageNum: 1,
          total: 30, // 添加total参数，默认值为30
          brand: this.carData.brand || ''
        };

        // 从store获取域名配置的主题过滤条件
        const themeFilters = this.$store.getters.domainConfig?.themeFilters || {};

        // 合并主题过滤参数
        const requestParams = { ...params, ...themeFilters };

        console.log(`[Detail] 获取相似车辆: brand=${params.brand}, total=30, themeFilters=${JSON.stringify(themeFilters)}`);

        // 调用searchCar接口
        const response = await this.$global.server.searchCar(requestParams);

        if (response && response.code === 200 && response.data && response.data.list) {
          // 处理API返回的数据
          this.similarCars = response.data.list.map(this.formatCarData);
          console.log(`[Detail] 获取到相似车辆: ${this.similarCars.length}`);
        } else {
          console.warn('[Detail] 获取相似车辆失败:', response?.message || '未知错误');
          this.similarCars = [];
        }
      } catch (error) {
        console.error('[Detail] 获取相似车辆数据出错:', error);
        this.similarCars = [];
      }
    },

    // 滚动到相似车辆部分
    scrollToSimilarCars () {
      // 只在移动端执行滚动
      if (this.deviceType === 'mobile') {
        this.$nextTick(() => {
          const el = document.querySelector('.detail__mobile-similar-pointer');
          if (el) {
            const top = el.getBoundingClientRect().top + window.pageYOffset - 90;
            window.scrollTo({ top, behavior: 'smooth' });
          }
        });
      }
    },
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.handleScroll);
  }
}
</script>

<style lang="scss" >
@import '~@/css/variables.scss';
@import '~@/css/ads.scss';

.detail {
	background: #fff;
	min-height: 100vh;

	&__container {
		max-width: rem(1200);
		margin: 0 auto;
		padding: rem(40) rem(20);
	}

	&__loading,
	&__error {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		min-height: 50vh;
		padding: rem(40);
	}

	&__loading-text,
	&__error-text {
		font-size: rem(18);
		text-align: center;
		margin-bottom: rem(20);
	}

	&__loading-text {
		color: #405ff2;
	}

	&__error-text {
		color: #ff3b30;
	}

	&__error-retry {
		background: #405ff2;
		color: white;
		border: none;
		padding: rem(10) rem(20);
		border-radius: rem(6);
		font-size: rem(16);
		cursor: pointer;
		transition: all 0.3s ease;

		&:hover {
			background: #2d4cd9;
		}
	}

	&__header {
		margin-bottom: rem(20);
	}

	&__title {
		font-size: rem(32);
		line-height: rem(40);
		color: #050b20;
		margin: 0 0 rem(8) 0;
	}

	&__brand {
		display: flex;
		align-items: center;
		gap: rem(12);
	}

	&__brand-logo {
		width: rem(32);
		height: rem(32);
		object-fit: contain;
	}

	&__brand-name {
		font-size: rem(18);
		color: #848998;
	}

	&__content {
		display: flex;
		gap: rem(32);
	}

	&__main {
		flex: 1;
	}

	&__gallery {
		background: #fff;
		border-radius: rem(16);
		overflow: hidden;
		margin-bottom: rem(30);
		box-shadow: 0 rem(4) rem(12) rgba(0, 0, 0, 0.05);
		position: relative;
	}

	&__gallery-main {
		width: 100%;
		height: fit-content;
		overflow: hidden;
		position: relative;
		cursor: pointer;
	}

	&__gallery-img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	&__gallery-indicator {
		position: absolute;
		bottom: rem(16);
		left: 50%;
		transform: translateX(-50%);
		background: rgba(64, 95, 242, 0.8);
		color: #fff;
		padding: rem(8) rem(16);
		border-radius: rem(20);
		font-size: rem(12);
		display: flex;
		align-items: center;
		gap: rem(6);
		z-index: 5;

		&-icon {
			width: rem(12);
			height: rem(12);
		}
	}

	&__thumbnails {
		position: absolute;
		bottom: rem(16);
		left: rem(16);
		display: flex;
		gap: rem(8);
		overflow-x: auto;
		overflow-y: hidden;
		max-width: calc(100% - rem(32));
		scrollbar-width: none; /* Firefox */
		-ms-overflow-style: none; /* IE and Edge */
		padding-bottom: rem(5); /* 为可能出现的滚动条留出空间 */

		/* 隐藏滚动条 - Webkit浏览器 */
		&::-webkit-scrollbar {
			display: none;
		}
	}

	&__thumbnail-item {
		width: rem(100);
		height: rem(67); /* 保持100:67的比例 */
		border-radius: rem(8);
		overflow: hidden;
		border: rem(2) solid transparent;
		cursor: pointer;
		flex-shrink: 0; /* 防止项目被压缩 */
		// opacity: 0.7;
		transition: all 0.3s ease;

		&--active {
			border-color: #405ff2;
			opacity: 1;
		}

		&:hover {
			opacity: 0.9;
		}
	}

	&__thumbnail-img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	&__sections {
		display: flex;
	}

	&__section-loading {
		width: 100%;
		padding: rem(30) 0;
		text-align: center;
	}

	&__section-loading-text {
		color: #405ff2;
		font-size: rem(16);
		line-height: rem(24);
	}

	&__timeline {
		width: rem(24);
		margin-right: rem(24);
		position: relative;

		&-line {
			position: absolute;
			top: rem(12);
			bottom: rem(12);
			left: 50%;
			width: 1px;
			// background-color: #e9e9e9;
			border-left: 1px dashed #405ff2;
			transform: translateX(-50%);
		}
	}

	&__sections-content {
		flex: 1;
	}

	&__section {
		margin-bottom: rem(30);
		padding-bottom: rem(16);
		border-bottom: 1px solid #e9e9e9;

		&:last-child {
			margin-bottom: 0;
			border-bottom: none;
		}
	}

	&__section-header {
		display: flex;
		align-items: center;
		margin-bottom: rem(16);
		position: relative;
	}

	&__timeline-dot {
		width: rem(10);
		height: rem(10);
		border-radius: 50%;
		background-color: #e9e9e9;
		position: absolute;
		left: rem(-41);
		z-index: 1;
		background-color: #405ff2;

		// &--active {
		// 	background-color: #405ff2;
		// 	transform: scale(1.2);
		// }
	}

	&__section-title {
		color: #050b20;

		font-size: rem(20);
		font-style: normal;
		font-weight: 500;
		line-height: rem(24); /* 120% */
		margin: 0;
	}

	&__section-content {
		color: #4c5164;

		font-size: rem(14);
		font-style: normal;
		font-weight: 400;
		line-height: rem(24); /* 171.429% */

		p {
			margin-bottom: rem(16);
		}

		ul {
			padding-left: rem(20);
			margin-bottom: rem(16);

			li {
				margin-bottom: rem(8);
			}
		}
	}

	&__sidebar {
		width: rem(320);
		flex-shrink: 0;
	}

	&__mobile-sidebar {
		margin-bottom: rem(30);
	}

	&__sidebar-card {
		background: #fff;
		border-radius: rem(16);
		border: 1px solid #e9e9e9;
		padding: 0 rem(20);
		position: sticky;
		top: rem(100);
	}

	&__sidebar-title {
		font-size: rem(20);
		color: #050b20;
		margin: 0 0 rem(24) 0;
	}

	&__spec-item {
		display: flex;
		align-items: center;
		padding: rem(20) 0;
		border-bottom: 1px solid #e9e9e9;
		&:last-child {
			border-bottom: none;
		}
	}

	&__spec-icon-wrapper {
		width: rem(32);
		height: rem(32);
		border-radius: rem(8);
		display: flex;
		align-items: center;
		justify-content: center;
		margin-right: rem(10);
		background: #405ff2;
	}

	&__spec-icon {
		width: rem(16);
		height: rem(16);
	}

	&__spec-content {
		flex: 1;
		flex-direction: column-reverse;
		display: flex;
		gap: rem(4);
	}

	&__spec-label {
		color: #4c5164;

		font-size: rem(12);
		font-style: normal;
		font-weight: 400;
		line-height: rem(12); /* 100% */
		text-transform: capitalize;
	}

	&__spec-value {
		font-size: rem(16);
		font-style: normal;
		font-weight: 500;
		line-height: rem(16); /* 100% */
		text-transform: capitalize;
		color: #050b20;
	}

	// 移除旧的价格块和评分区域样式
	&__price-block {
		display: none;
	}

	&__ratings {
		display: none;
	}

	// 移动端相似车辆垂直列表样式
	&__mobile-similar-horizontal {
		margin-bottom: rem(30);
	}

	&__similar-horizontal-title {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: rem(20);
		padding: 0 rem(16);

		h2 {
			font-size: rem(24);
			line-height: rem(24);
			color: #050b20;
			font-weight: 900;
			margin: 0;
		}
	}

	&__similar-horizontal-list {
		display: flex;
		flex-direction: row;
		gap: rem(16);
		padding: 0 rem(16);
		overflow-x: auto;
		scrollbar-width: none; /* Firefox */
		-ms-overflow-style: none; /* IE and Edge */
		padding-bottom: rem(10);

		&::-webkit-scrollbar {
			display: none; /* Chrome, Safari, Opera */
		}
	}

	&__similar-horizontal-item {
		flex: 0 0 rem(280);
		border-radius: rem(12);
		overflow: hidden;
		background: #ffffff;
		box-shadow: 0 rem(4) rem(8) rgba(0, 0, 0, 0.05);
	}

	&__similar-horizontal-image {
		position: relative;

		img {
			width: 100%;
			height: rem(180);
			object-fit: cover;
			display: block;
		}
	}

	&__similar-horizontal-speed {
		position: absolute;
		top: rem(8);
		left: rem(8);
		background: rgba(0, 0, 0, 0.5);
		border-radius: rem(6);
		color: white;
		padding: rem(4) rem(8);
		font-size: rem(12);
		font-weight: bold;
		display: flex;
		align-items: center;

		span {
			margin-left: rem(6);
			font-size: rem(10);
			font-weight: normal;
		}
	}

	&__similar-horizontal-info {
		padding: rem(16);
	}

	&__similar-horizontal-header {
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		margin-bottom: rem(10);
	}

	&__similar-horizontal-model {
		font-size: rem(16);
		color: #050b20;
		margin-bottom: rem(4);
	}

	&__similar-horizontal-brand {
		font-size: rem(12);
		color: #959eac;
	}

	&__similar-horizontal-logo {
		width: rem(32);
		height: rem(32);
	}

	&__similar-horizontal-rating {
		display: flex;
		align-items: center;
		gap: rem(4);
		margin-bottom: rem(10);
		color: #4c5164;
		font-size: rem(12);

		img {
			width: rem(14);
			height: rem(14);
		}
	}

	&__similar-horizontal-rate {
		color: #405ff2;
		font-weight: bold;
	}

	&__similar-horizontal-price {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	&__similar-horizontal-price-text {
		font-size: rem(14);
		color: #050b20;
	}

	&__similar-horizontal-price-link {
		width: rem(16);
		height: rem(16);
		display: flex;
		align-items: center;
		justify-content: center;

		img {
			width: 100%;
			height: 100%;
		}
	}

	&__mobile-similar-vertical {
		margin-top: rem(40);
	}

	&__similar-vertical-title {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: rem(20);
		padding: 0 rem(16);

		h2 {
			font-size: rem(24);
			line-height: rem(24);
			color: #050b20;
			margin: 0;
		}
	}

	&__view-all {
		display: flex;
		align-items: center;
		gap: rem(6);
		color: #405ff2;

		font-size: rem(14);

		img {
			width: rem(14);
			height: rem(14);
		}
	}

	&__similar-vertical-list {
		display: flex;
		flex-direction: column;
		gap: rem(16);
		padding: 0 rem(16);
	}

	&__similar-vertical-item {
		border-radius: rem(12);
		overflow: hidden;
		background: #ffffff;
		border: 1px solid #e9e9e9;
		cursor: pointer;
		position: relative;
	}

	&__similar-vertical-pic {
		width: 100%;
		height: rem(185);
		object-fit: cover;
		z-index: 1;
	}

	&__similar-vertical-speed {
		position: absolute;
		top: rem(6);
		left: rem(6);
		z-index: 2;
		display: flex;
		background: rgba(0, 0, 0, 0.5);
		border-radius: rem(8);
		overflow: hidden;
	}

	&__similar-vertical-speed-value {
		background: rgba(0, 0, 0, 0.5);
		padding: rem(4) rem(8);
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: center;
	}

	&__similar-vertical-speed-number {
		color: #ffffff;
		text-align: center;
		font-size: rem(12);
		line-height: rem(12);
		font-weight: 700;
	}

	&__similar-vertical-speed-label {
		padding: rem(4) rem(8);
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: center;
	}

	&__similar-vertical-speed-text {
		color: #ffffff;
		text-align: center;
		font-size: rem(10);
		line-height: rem(10);
		font-weight: 400;
	}

	&__similar-vertical-baseinfo {
		background: rgba(255, 255, 255, 0.5);
		backdrop-filter: blur(10px);
		border-radius: rem(12);
		padding: rem(16);
		display: flex;
		flex-direction: column;
		gap: rem(10);
		align-items: flex-start;
		justify-content: flex-start;
		width: 100%;
		z-index: 2;
	}

	&__similar-vertical-info {
		display: flex;
		flex-direction: column;
		gap: rem(16);
		align-items: flex-start;
		justify-content: flex-start;
		align-self: stretch;
		flex-shrink: 0;
		position: relative;
	}

	&__similar-vertical-card-title {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		align-self: stretch;
		flex-shrink: 0;
		position: relative;
	}

	&__similar-vertical-name {
		display: flex;
		flex-direction: column;
		gap: rem(8);
		align-items: flex-start;
		justify-content: flex-start;
		position: relative;
	}

	&__similar-vertical-model {
		color: #050b20;
		text-align: left;
		font-size: rem(16);
		line-height: rem(16);
		font-weight: 700;
		position: relative;
		display: flex;
		align-items: center;
		justify-content: flex-start;
		overflow: hidden;
		display: -webkit-box;
		text-overflow: ellipsis;
		-webkit-line-clamp: 1;
		-webkit-box-orient: vertical;
	}

	&__similar-vertical-brand {
		color: #848998;
		text-align: left;
		font-size: rem(12);
		line-height: rem(12);
		font-weight: 400;
		position: relative;
		display: flex;
		align-items: center;
		justify-content: flex-start;
	}

	&__similar-vertical-logo {
		flex-shrink: 0;
		width: rem(36);
		height: rem(36);
		background: #fff;
		border-radius: 50%;
		padding: rem(2);
		object-fit: contain;
		position: relative;
	}

	&__similar-vertical-divider {
		background: #050b20;
		opacity: 0.1;
		align-self: stretch;
		flex-shrink: 0;
		height: rem(1);
		position: relative;
	}

	&__similar-vertical-type {
		display: flex;
		flex-direction: column;
		gap: rem(8);
		align-items: flex-start;
		justify-content: flex-start;
		align-self: stretch;
		flex-shrink: 0;
		position: relative;
	}

	&__similar-vertical-type-item {
		display: flex;
		flex-direction: row;
		gap: rem(9);
		align-items: center;
		justify-content: flex-start;
		flex-shrink: 0;
		position: relative;
	}

	&__similar-vertical-type-icon {
		flex-shrink: 0;
		width: rem(12);
		height: rem(12);
		position: relative;
	}

	&__similar-vertical-type-text {
		color: #050b20;
		text-align: left;
		font-size: rem(12);
		line-height: rem(12);
		font-weight: 400;
		position: relative;
		display: flex;
		align-items: center;
		justify-content: flex-start;
	}

	&__similar-vertical-price {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		align-self: stretch;
		flex-shrink: 0;
		position: relative;
	}

	&__similar-vertical-price-text {
		color: #050b20;
		text-align: left;
		font-size: rem(18);
		line-height: rem(18);
		font-weight: 700;
		position: relative;
		display: flex;
		align-items: center;
		justify-content: flex-start;
	}

	&__similar-vertical-price-link {
		border-radius: rem(50%);
		width: rem(16);
		height: rem(16);
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
	}

	&__similar-vertical-arrow {
		flex-shrink: 0;
		width: rem(16);
		height: rem(16);
		position: relative;
		overflow: visible;
	}
}

// 响应式样式 - PC设备
.detect_deviece_pc,
.detect_deviece_desktop {
	.detail {
		--fontsize-base: #{$vw_fontsize_pc};
		font-size: calc(var(--fontsize-base) / #{$vw_design_pc} * 100vw);
	}
}

// 响应式样式 - 移动设备
.detect_deviece_mobile {
	.detail {
		--fontsize-base: #{$vw_fontsize_mobile};
		font-size: calc(var(--fontsize-base) / #{$vw_design_mobile} * 100vw);

		&__container {
			padding: rem(20) 0;
		}

		&__header {
			padding: 0 rem(16);
		}

		&__content {
			flex-direction: column;
		}

		// 为detail__main下的所有直接子元素设置默认padding
		&__main {
			& > * {
				padding-left: rem(16);
				padding-right: rem(16);
			}

			// 重置不需要padding的特定元素
			& > .detail__mobile-similar-horizontal,
			& > .detail__mobile-similar-vertical {
				padding-left: 0;
				padding-right: 0;
			}
		}

		&__title {
			color: #050b20;

			font-size: rem(40);
			font-style: normal;
			font-weight: 900;
			line-height: rem(40); /* 100% */
			margin-bottom: rem(10);
		}
		&__brand {
			gap: rem(10);
		}

		&__gallery {
			position: relative;
			padding-bottom: rem(80); // 为缩略图区域预留空间
			box-shadow: none;
			border-radius: 0;
		}

		&__gallery-main {
			height: rem(240);
			border-radius: rem(16);
		}

		&__thumbnails {
			position: absolute;
			bottom: 0;
			left: 0;
			width: 100%;
			height: rem(80);
			padding: rem(16);
			display: flex;
			gap: rem(8);
			overflow-x: auto;
			max-width: 100%;
			background-color: #fff;
			border-radius: 0 0 rem(16) rem(16);
			scrollbar-width: none; /* Firefox */
			-ms-overflow-style: none; /* IE and Edge */
			justify-content: flex-start;

			&::-webkit-scrollbar {
				display: none; /* Chrome, Safari, Opera */
			}
		}

		&__thumbnail-item {
			width: rem(80);
			height: rem(48);
			flex: 0 0 auto;
		}

		&__sections {
			margin-top: rem(30);
			margin-bottom: rem(30);
		}

		&__timeline {
			width: rem(10);
			margin-right: rem(10);
		}

		&__timeline-dot {
			width: rem(10);
			height: rem(10);
			left: rem(-20);
		}

		&__section-title {
			font-size: rem(20);
		}

		&__section-content {
			font-size: rem(14);
			line-height: rem(22);
		}

		&__spec-icon-wrapper {
			width: rem(36);
			height: rem(36);
		}

		&__view-all {
			font-size: rem(14);
			line-height: rem(14);

			img {
				width: rem(14);
				height: rem(14);
			}
		}

		&__similar-vertical-model {
			font-size: rem(14);
			line-height: rem(14);
		}

		&__similar-vertical-brand {
			font-size: rem(10);
			line-height: rem(10);
		}

		&__similar-vertical-type-text {
			font-size: rem(10);
			line-height: rem(10);
		}

		&__similar-vertical-price-text {
			font-size: rem(10);
			line-height: rem(10);
		}
	}
}
</style>
